import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { httpService } from '../utils/apiService';

export const useLogin = (options = {}) => {
  return useMutation({
    mutationKey: ['LOGIN_USER'],
    mutationFn: async ({ email, password }) => {
      const response = await httpService.post('team-users/login', {
        email,
        password,
      });
      return response.data.data;
    },

    ...options,
  });
};

export const useFetchDashboardData = (token, clientId) => {
  return useQuery({
    queryKey: ['dashboard', clientId],
    queryFn: async () => {
      let data = {
        clientId: clientId,
      };
      const response = await httpService.post(`dashboard`, token, data || {});

      return response.data;
    },
    enabled: !!token,
  });
};

export const useFetchNotifications = (token, clientId, date, unRead) => {
  return useInfiniteQuery(
    ['notifications', clientId, date, unRead],
    async ({ pageParam = 1 }) => {
      const params = new URLSearchParams({
        clientId,
        offset: pageParam,
        limit: 20,
      });
      const response = await httpService.post(
        `message/list-message?${params}`,
        token,
        {
          clientId,
          ...date,
        }
      );

      return response.data.data;
    },
    {
      enabled: !!token,
      getNextPageParam: (lastPage, allPages) => {
        const nextPageNumber = allPages?.length || 0 + 1;
        const totalPages = Math.ceil(lastPage?.totalCount / 20);

        return nextPageNumber <= totalPages ? nextPageNumber : undefined;
      },
      refetchIntervalInBackground: 40000,
      refetchInterval: 40000,
    }
  );
};

export const useReadUpdateMessage = (messageIds, token, clientId, refetch) => {
  return useMutation({
    mutationKey: ['readMessage'],
    mutationFn: async () => {
      const response = await httpService.post(
        `message/read-update-message`,
        token,
        {
          clientId,
          messageIds,
        }
      );

      return response.data;
    },
    onSuccess: () => {
      refetch();
    },
    enabled: !!token,
  });
};

export const useFetchOperatorNames = ({ page, limit, search }) => {
  return useQuery({
    queryKey: ['listOperators', page, search],
    queryFn: async () => {
      const response = await httpService.get(
        `/operator/list?limit=${limit}&offset=${page}&search=${search}`
      );
      return response.data.data;
    },
    refetchOnWindowFocus: false,
  });
};

export const useGetDashboardCharger = (
  operatorId,
  locationOwnerId,
  revenueData,
  consumeUnitsData,
  chargingHistoryData
) => {
  let queryKey;
  if (operatorId && !locationOwnerId) {
    queryKey = [
      'getChargerDashboard',
      operatorId,
      revenueData,
      consumeUnitsData,
      chargingHistoryData,
    ];
  } else if (operatorId && locationOwnerId) {
    queryKey = [
      'getChargerDashboard',
      operatorId,
      locationOwnerId,
      revenueData,
      consumeUnitsData,
      chargingHistoryData,
    ];
  } else {
    queryKey = [
      'getChargerDashboard',
      revenueData,
      consumeUnitsData,
      chargingHistoryData,
    ];
  }

  return useQuery({
    queryKey,
    queryFn: async () => {
      const requestBody = {
        operatorId,
        locationOwnerId,
        revenueData,
        consumeUnitsData,
        chargingHistoryData,
      };

      const response = await httpService.post('/dashboard', requestBody);
      return response.data.data;
    },
    refetchOnWindowFocus: false,
  });
};

export const useDashboardLocationAnalysis = (
  operatorId,
  locationOwnerId,
  revenueData,
  consumeUnitsData,
  chargingHistoryData
) => {
  let queryKey;
  if (operatorId && !locationOwnerId) {
    queryKey = [
      'listLocationAnalysis',
      operatorId,
      revenueData,
      consumeUnitsData,
      chargingHistoryData,
    ];
  } else if (operatorId && locationOwnerId) {
    queryKey = [
      'listLocationAnalysis',
      operatorId,
      locationOwnerId,
      revenueData,
      consumeUnitsData,
      chargingHistoryData,
    ];
  } else {
    queryKey = [
      'listLocationAnalysis',
      revenueData,
      consumeUnitsData,
      chargingHistoryData,
    ];
  }

  return useQuery({
    queryKey,
    queryFn: async () => {
      const requestBody = {
        operatorId,
        locationOwnerId,
        revenueData,
        consumeUnitsData,
        chargingHistoryData,
      };

      const response = await httpService.post(
        `/location/analysis`,
        requestBody
      );
      return response.data.data;
    },
    refetchOnWindowFocus: false,
  });
};

export const useOCPPLogs = (id, numberOfLines = 100, options={}) => {
  return useQuery({
    queryKey: ['getOCPP', id, numberOfLines],
    queryFn: async () => {
      if (!id) return { data: [] };
      const response = await httpService.get(
        `/charger/ocpp-logs/${id}?numberOfLines=${numberOfLines}`
      );
      return response.data;
    },
    retry: 1,
    ...options,
    refetchOnWindowFocus: false,
  });
};

export const useOCPPOldLogs = (operatorId, chargerId, page) => {
  return useQuery({
    queryKey: ['getOldOCPP', operatorId, chargerId, page],
    queryFn: async () => {
      const response = await httpService.post(
        `/ocpp-logs/get-old-ocpp-logs?limit=10&offset=${page}`,
        {
          operatorId,
          chargerId,
        }
      );
      return response.data.data;
    },
    retry: 1,
    enabled: !!chargerId && !!operatorId,
    refetchOnWindowFocus: false,
  });
};

export const useChangePassword = (options = {}) => {
  return useMutation({
    mutationKey: ['CHANGE_PASSWORD'],
    mutationFn: async (payload) => {
      const response = await httpService.post(
        `/team-users/change-password`,
        payload
      );

      return response.data;
    },
    ...options,
  });
};

export const useRequestOTP = (options = {}) => {
  return useMutation({
    mutationKey: ['REQUEST_OTP'],
    mutationFn: async (payload) => {
      const response = await httpService.post(
        `/team-users/forgot-password`,
        payload
      );
      return response.data;
    },
    ...options,
  });
};

export const useResetPassword = (options = {}) => {
  return useMutation({
    mutationKey: ['RESET_OTP'],
    mutationFn: async (payload) => {
      const response = await httpService.post(
        `/team-users/reset-password`,
        payload
      );
      return response.data;
    },
    ...options,
  });
};

export const useUpdateLanguage = (options = {}) => {
  return useMutation({
    mutationKey: ['UpdateLang'],
    mutationFn: async (payload) => {
      const response = await httpService.post(
        `/team-users/update-language`,
        payload
      );
      return response.data;
    },
    ...options,
  });
};
export const useFilUpload = (options = {}) => {
  return useMutation({
    mutationKey: ['FileUpload'],
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('file', file); // Ensure correct `file` field

      const response = await httpService.post(`/upload`, formData, {
        isFormData: true, // Pass flag to httpService to handle multipart/form-data
        headers: httpService.getHeaders(true), // Ensure token is always included
      });

      return response.data.data; // Returns the uploaded file S3 URL
    },
    ...options,
  });
};

export const fetchLocationOwners = async ({ operatorId, search, page = 1 }) => {
  if (!operatorId || !search) return { owners: [], totalCount: 0 };

  try {
    const response = await httpService.post(
      `location/get-location-owner-select?search=${search}&page=${page}`,
      { operatorId }
    );

    const owners = response.data.data.paginatedData.map((owner) => ({
      label: `${owner.fname} ${owner.lname} (${owner.email})`, // Display name + email
      value: owner._id, // Use `_id` as value
      fname: owner.fname,
      lname: owner.lname,
      email: owner.email,
      phone: owner.phone,
    }));

    return {
      owners,
      totalCount: response.data.data.totalCount || 0,
    };
  } catch (error) {
    console.error('Error fetching residential area autoparks:', error);
    return { owners: [], totalCount: 0 };
  }
};

export const useGetRevenueGraph = (operatorId, locationOwnerId) => {
  let queryKey;
  if (operatorId && !locationOwnerId) {
    queryKey = ['getRevenueGraph', operatorId];
  } else if (operatorId && locationOwnerId) {
    queryKey = ['getRevenueGraph', operatorId, locationOwnerId];
  } else {
    queryKey = ['getRevenueGraph'];
  }

  return useQuery({
    queryKey,
    queryFn: async () => {
      const requestBody = {
        operatorId,
        locationOwnerId,
      };
      const response = await httpService.get('/dashboard/revenue-graph', requestBody);
      return response.data.data;
    },
    refetchOnWindowFocus: false,
  });
};

export const useGetSessionsGraph = (operatorId, locationOwnerId, filter) => {
  let queryKey;
  if (operatorId && !locationOwnerId) {
    queryKey = ['getSessionsGraph', operatorId, filter];
  } else if (operatorId && locationOwnerId) {
    queryKey = ['getSessionsGraph', operatorId, locationOwnerId, filter];
  } else {
    queryKey = ['getSessionsGraph', filter];
  }

  return useQuery({
    queryKey,
    queryFn: async () => {
      const requestBody = {
        operatorId,
        locationOwnerId,
        ...filter,
      };
      const response = await httpService.get('/dashboard/sessions-graph', requestBody);
      return response.data.data;
    },
    refetchOnWindowFocus: false,
  });
};

export const useGetEnergyUtilizationGraph = (operatorId, locationOwnerId, filter) => {
  let queryKey;
  if (operatorId && !locationOwnerId) {
    queryKey = ['getEnergyUtilizationGraph', operatorId, filter];
  } else if (operatorId && locationOwnerId) {
    queryKey = ['getEnergyUtilizationGraph', operatorId, locationOwnerId, filter];
  } else {
    queryKey = ['getEnergyUtilizationGraph', filter];
  }

  return useQuery({
    queryKey,
    queryFn: async () => {
      const requestBody = {
        operatorId,
        locationOwnerId,
        ...filter,
      };
      const response = await httpService.get('/dashboard/usage-graph', requestBody);
      return response.data.data;
    },
    refetchOnWindowFocus: false,
  });
};

export const useOCPPHealthCheck = (options = {}) => {
  return useQuery({
    queryKey: ['ocppHealth'],
    queryFn: async () => {
      const response = await httpService.get(import.meta.env.VITE_OCPP_HEALTH_CHECK_URL);
      return response.status === 200;
    },
    refetchInterval: 30000, // Check every 30 seconds
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useFetchCronList = (params = {}, options = {}) => {
  const {
    status,
    startDate,
    endDate,
    hasError,
    limit = 20,
    offset = 1,
    name = null,
    type = null,
  } = params;

  return useQuery({
    queryKey: ['cronList', status, startDate, endDate, hasError, limit, offset, name, type],
    queryFn: async () => {
      const queryParams = {
        ...(status && { status }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(hasError !== undefined && { hasError }),
        limit,
        offset,
        ...(name && { name }),
        ...(type && { type }),
      };

      const response = await httpService.get('cron/list', queryParams);
      return response.data.data || response.data;
    },
    refetchOnWindowFocus: false,
    ...options,
  });
};